import { useEffect } from 'react'
import { useLocation, useMatch, useNavigate } from 'react-router'

import { CHARTS_2_URL } from 'features/charts-v2/paths'
import { FEEDBACK_URL } from 'features/feedback/paths'
import { useFeatureFlags } from 'features/project'
import { useProjectStore } from 'features/project/store'
import { isInitialFlow } from 'features/project/utils/project-status'
import { SETTINGS_URL } from 'features/settings/paths'
import { ProjectState } from 'types/project'

/**
 * Handles initial navigation redirects when entering a project.
 *
 * Three redirect scenarios:
 *   1. If the project is in its initial setup flow and the user isn't already
 *      on the settings page, redirect them to settings.
 *   2. Legacy `dashboard-new` URLs are renamed to `dashboard`.
 *   3. If the user lands on the bare `/project/:id` URL (no sub-route),
 *      redirect to the first visible page based on clientSettings.
 */
export const useProjectRedirects = (): void => {
  const details = useProjectStore((state: ProjectState) => state.details)
  const location = useLocation()
  const navigate = useNavigate()
  const match = useMatch('/project/:id')
  const isExact = !!match && match.pathname === match.pathnameBase
  const { data: featureFlags } = useFeatureFlags()
  const hideAskYogi = !!(featureFlags as Record<string, boolean> | undefined)?.[
    'hide_ask_yogi'
  ]

  useEffect(() => {
    // 1. Force settings page during initial setup flow
    if (
      isInitialFlow(details.state) &&
      location.pathname.indexOf(SETTINGS_URL) === -1
    ) {
      navigate(`${location.pathname}/${SETTINGS_URL}`)
      return
    }

    // 2. Rename legacy `dashboard-new` → `dashboard`
    if (location.pathname.includes('dashboard-new')) {
      navigate(location.pathname.replace('dashboard-new', 'dashboard'))
    }

    // 3. Redirect bare /project/:id to the first visible page
    //    If home (compass) is enabled, no redirect needed — it renders via the index route.
    //    Exception: if hide_ask_yogi is set, skip home and redirect to Feedback.
    if (isExact) {
      const settings = details.clientSettings.project.pages
      // hide_ask_yogi: This is to hide Ask Yogi for Disney. Delete these checks once they approve Ask Yogi usage.
      if (hideAskYogi) {
        navigate(`${location.pathname}/${FEEDBACK_URL}${location.search}`, {
          replace: true,
        })
      } else if (!settings?.compass?.hidden) {
        // Home page renders at the index route, no redirect needed
      } else if (!settings?.dashboard?.hidden) {
        navigate(`${location.pathname}/${CHARTS_2_URL}${location.search}`, {
          replace: true,
        })
      } else {
        const validRoute = Object.keys(settings).find(
          (key: string) =>
            !(settings as unknown as Record<string, { hidden?: boolean }>)[key]
              ?.hidden,
        )
        navigate(`${location.pathname}/${validRoute}${location.search}`)
      }
    }
  }, [
    details.proj_uuid,
    location.pathname,
    location.search,
    details.clientSettings.project.pages,
    details.state,
    isExact,
    hideAskYogi,
    navigate,
  ])
}
